import type SchedulingPolicy from 'owa-service/lib/contract/SchedulingPolicy';
import type { BookingServiceWindow, BookingSchedulingPolicy } from 'owa-graph-schema';
import mapB2BookingServiceWindow from './mapB2BookingServiceWindow';
import getDurationInMinutes from '../utils/getDurationInMinutes';
import getDurationInDays from '../utils/getDurationInDays';
import type FeatureSource from '../constants/source';
import isValidISOString from '../utils/isValidISO';

const DEFAULT_MINIMUM_LEAD_TIME = 'P1D';
const DEFAULT_MAXIMUM_ADVANCE = 'P365D';
const DEFAULT_TIME_SLOT_INTERVAL = 'PT30M';

const DEFAULT_MINIMUM_LEAD_TIME_BWM = 'PT1H';
const DEFAULT_MAXIMUM_ADVANCE_BWM = 'P90D';

const mapBookingSchedulingPolicy = (
    schedulingPolicy: BookingSchedulingPolicy,
    source?: string
): SchedulingPolicy | undefined => {
    if (!schedulingPolicy) {
        if (source === 'BookWithMe') {
            const minLeadTimeinMinutes = getDurationInMinutes(DEFAULT_MINIMUM_LEAD_TIME_BWM);
            const bwmDefaultSchedulingPolicy: SchedulingPolicy = {
                LeadTimeInMinutes: minLeadTimeinMinutes,
                LeadTimeForBookingsInMinutes: minLeadTimeinMinutes,
                LeadTimeForCancellationsInMinutes: minLeadTimeinMinutes,
                EndOfBookingWindowInDays: getDurationInDays(DEFAULT_MAXIMUM_ADVANCE_BWM),
                TimeSlotIntervalInMinutes: getDurationInMinutes(DEFAULT_TIME_SLOT_INTERVAL),
                SendConfirmationsToMailboxOwner: true,
                AllowStaffSelection: false,
                ServiceBookableWindows: undefined,
                SendMeetingInviteToCustomer: false,
            };
            return bwmDefaultSchedulingPolicy;
        } else {
            return;
        }
    }
    const {
        allowStaffSelection,
        maximumAdvance,
        minimumLeadTime,
        sendConfirmationsToOwner,
        timeSlotInterval,
        hasCustomServiceWindows,
        bookingServiceWindows,
        sendMeetingInviteToCustomer,
    } = schedulingPolicy;

    const sanitizedminLeadTime = isValidISOString(minimumLeadTime)
        ? minimumLeadTime
        : source === 'BookWithMe'
        ? DEFAULT_MINIMUM_LEAD_TIME_BWM
        : DEFAULT_MINIMUM_LEAD_TIME;

    const modifiedSchedulingPolicy: SchedulingPolicy = {
        LeadTimeInMinutes:
            source === 'BookWithMe'
                ? getDurationInMinutes(sanitizedminLeadTime ?? DEFAULT_MINIMUM_LEAD_TIME_BWM)
                : getDurationInMinutes(sanitizedminLeadTime ?? DEFAULT_MINIMUM_LEAD_TIME),
        LeadTimeForBookingsInMinutes:
            source === 'BookWithMe'
                ? getDurationInMinutes(sanitizedminLeadTime ?? DEFAULT_MINIMUM_LEAD_TIME_BWM)
                : getDurationInMinutes(sanitizedminLeadTime ?? DEFAULT_MINIMUM_LEAD_TIME),
        LeadTimeForCancellationsInMinutes:
            source === 'BookWithMe'
                ? getDurationInMinutes(sanitizedminLeadTime ?? DEFAULT_MINIMUM_LEAD_TIME_BWM)
                : getDurationInMinutes(sanitizedminLeadTime ?? DEFAULT_MINIMUM_LEAD_TIME),
        EndOfBookingWindowInDays:
            source === 'BookWithMe'
                ? getDurationInDays(maximumAdvance ?? DEFAULT_MAXIMUM_ADVANCE_BWM)
                : getDurationInDays(maximumAdvance ?? DEFAULT_MAXIMUM_ADVANCE),
        TimeSlotIntervalInMinutes: getDurationInMinutes(
            timeSlotInterval ?? DEFAULT_TIME_SLOT_INTERVAL
        ),
        SendConfirmationsToMailboxOwner: sendConfirmationsToOwner ?? true,
        AllowStaffSelection: allowStaffSelection ?? true,
        ServiceBookableWindows: hasCustomServiceWindows
            ? mapB2BookingServiceWindow(bookingServiceWindows as BookingServiceWindow[])
            : undefined,
        SendMeetingInviteToCustomer: sendMeetingInviteToCustomer ?? false,
    };

    return modifiedSchedulingPolicy;
};

export default mapBookingSchedulingPolicy;
